// DS4.4
import React from 'react';
import c from 'classnames';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import InputHint from '../../lego/InputHint';
import InputLabelSubText from '../../lego/InputLabelSubText';
import InputLabelText from '../../lego/InputLabelText';
import InputErrorMessage from '../../lego/InputErrorMessage';
import InputSuccessMessage from '../../lego/InputSuccessMessage';
import FormElement, { FormElementProps } from '../../layouts/FormElement';
import Checkbox from '../../../Checkbox';
import MainToggle from '../MainToggle';

import s from './BooleanInput.scss';

export interface Props extends Pick<FormElementProps, 'noBottomSpacing'> {
  /** Required for a11y reasons. We need to be able to link autocomplete menus and labels to the input field. */
  id: string;

  /**
   * Displays above the input field.
   *
   * Required for a11y reasons. If design won't work a label into the spec, consider passing through `isLabelHidden`.
   */
  label: React.ReactNode;

  /** Displays below the `label` in smaller font */
  subLabel?: string;

  /**
   * Displays below the input field as an InputHint.
   */
  details?: React.ReactNode;

  /** `name` attribute to be used on <input>. Optional. Only applies when `type` is `checkbox`. */
  name?: string;

  disabled?: boolean;
  type?: 'checkbox' | 'toggle';
  size?: 'small' | 'large';
  validationResult?: 'success' | 'error';
  className?: string;
  labelClassName?: string;
  value?: boolean;
  biData?: BiEvent;
  tabIndex?: number;
  onChange: (value: boolean, e?: any) => void;
  innerRef?: (node: HTMLElement | null) => void;
}

/**
 * A boolean field, either in toggle or checkbox format.
 */
const BooleanInput = React.forwardRef<HTMLDivElement, React.PropsWithChildren<Props>>(
  (
    {
      id,
      name,
      label,
      subLabel,
      details,
      className,
      labelClassName,
      validationResult,
      noBottomSpacing,
      innerRef,
      biData,
      tabIndex,
      disabled = false,
      value = false,
      size = 'small',
      type = 'checkbox',
      onChange,
    },
    ref
  ) => {
    let wrappedDetails: React.ReactNode;
    const errorMessageId = `${id}-error`;
    const hintId = `${id}-hint`;
    const onLabelClick = (ev: React.MouseEvent<HTMLElement>) => {
      // If a link inside the label was clicked, avoid changing the checkbox value
      const target = ev.target as HTMLElement;
      if (target.tagName.toLowerCase() === 'a') {
        ev.stopPropagation();
        return;
      }
      if (!disabled) {
        onChange(!value, ev);
      }
    };

    switch (validationResult) {
      case 'success':
        wrappedDetails = details && <InputSuccessMessage>{details}</InputSuccessMessage>;
        break;
      case 'error':
        wrappedDetails = details && (
          <InputErrorMessage id={errorMessageId}>{details}</InputErrorMessage>
        );
        break;
      default:
        wrappedDetails = details && <InputHint id={hintId}>{details}</InputHint>;
    }

    return (
      <FormElement
        className={c(className, s[`size-${size}`], disabled && s['disabled'])}
        inputId={id}
        label={label}
        isLabelHidden={true}
        subLabel={subLabel && <InputLabelSubText>{subLabel}</InputLabelSubText>}
        details={wrappedDetails}
        tabIndex={tabIndex}
        noBottomSpacing={noBottomSpacing}
        ref={ref}
      >
        <div className={s['wrapper']}>
          {type === 'checkbox' && (
            <Checkbox
              ariaLabelledBy={`${id}-label`}
              // checkbox has small and big (no medium or large)
              size={size === 'small' ? 'small' : 'big'}
              onChange={onChange}
              isChecked={value}
              disabled={disabled}
              name={name}
              biData={biData}
              tabIndex={tabIndex}
              innerRef={innerRef}
            />
          )}
          {type === 'toggle' && (
            <MainToggle
              variant="main"
              aria-labelledby={`${id}-label`}
              size={'small'}
              onChange={onChange}
              value={value}
              tabIndex={tabIndex}
              disabled={disabled}
              biData={biData}
              innerRef={innerRef}
            />
          )}
          <InputLabelText className={c(s['label-text'], labelClassName)} onClick={onLabelClick}>
            {label}
          </InputLabelText>
        </div>
      </FormElement>
    );
  }
);
BooleanInput.displayName = 'BooleanInput';

export default BooleanInput;
